import { Alert, Stack } from "@mui/material";
import {
  ControlledModificationReasonField,
  useIsModificationReasonSatisfied,
} from "@src/appV2/ExperimentalHomeHealth/components/ModificationReasonField";
import { useTu } from "@src/appV2/i18n/useTu";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_USER_EVENTS,
  closeUiModalWithReason,
  UI_LIFECYCLE_CLOSE_REASONS,
} from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import { useIsAdmin } from "../../lib/useIsAdmin";
import { useLogHomeHealthEvent } from "../../lib/useLogHomeHealthEvent";
import { useUpdateCase } from "../api/useUpdateCase";
import { type Case, CaseStatus } from "../types";

interface Props {
  caseToReopen: Case | undefined;
  modalState: UseModalState;
}

/**
 * Mobile reopen confirmation, mirroring the desktop {@link CaseReopenDialog}:
 * PATCHes status back to OPEN (with modification reason) so the case returns to
 * the Active tab, rendered with {@link ResponsiveDialog} chrome.
 */
export function MobileCaseReopenDialog({ caseToReopen, modalState }: Props) {
  const { tu } = useTu("homeHealth");
  const { showSuccessToast } = useToast();
  const workplaceId = useCurrentUserId() ?? "";
  const isAdmin = useIsAdmin();
  const logHomeHealthEvent = useLogHomeHealthEvent();

  const caseId = caseToReopen?.id ?? "";
  const { mutateAsync: updateCase, isLoading, isError } = useUpdateCase(caseId);

  const [modificationReason, setModificationReason] = useState("");
  const isModificationReasonSatisfied = useIsModificationReasonSatisfied(modificationReason);

  // Reset state when the dialog closes so reopening starts clean.
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setModificationReason("");
    }
  }, [modalState.modalIsOpen]);

  async function handleReopenCase() {
    if (!caseToReopen) {
      return;
    }

    try {
      await updateCase({
        data: {
          type: "case",
          attributes: { status: CaseStatus.OPEN, modificationReason },
        },
      });
      logHomeHealthEvent(APP_V2_USER_EVENTS.HOME_HEALTH_CASE_REOPENED, {
        workplaceId,
        caseId,
        patientId: caseToReopen.attributes.patient.id,
        performedByAdmin: isAdmin,
      });
      showSuccessToast(tu("Case reopened successfully"));
      closeUiModalWithReason({
        modalState,
        reason: UI_LIFECYCLE_CLOSE_REASONS.submitSuccess,
      });
    } catch {
      // Error surfaced inline via the Alert below.
    }
  }

  return (
    <ResponsiveDialog
      modalState={modalState}
      title={tu("Reopen case")}
      footerContent={
        <Stack spacing={3} sx={{ paddingX: 5, paddingTop: 3, paddingBottom: 5 }}>
          <Button
            fullWidth
            variant="primary"
            disabled={isLoading || !isModificationReasonSatisfied}
            onClick={() => {
              void handleReopenCase();
            }}
          >
            {tu("Reopen")}
          </Button>
          <Button
            fullWidth
            variant="secondary"
            disabled={isLoading}
            onClick={modalState.closeModal}
          >
            {tu("Cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Text variant="body1">{tu("Are you sure you want to reopen this case?")}</Text>
        {/* PHI: kept out of translated copy and masked for Datadog RUM. */}
        <Text variant="body1" semibold data-dd-privacy="mask">
          {caseToReopen?.attributes.patient.attributes.externalPatientIdentifier}
        </Text>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          {tu('It will be moved back to the "Active" tab.')}
        </Text>
        <ControlledModificationReasonField
          value={modificationReason}
          onChange={setModificationReason}
        />
        {isError && <Alert severity="error">{tu("Error reopening case")}</Alert>}
      </Stack>
    </ResponsiveDialog>
  );
}
